import { Auth, CognitoUser } from "@aws-amplify/auth";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import apiClient from "src/utils/apiClient";
import { Hub } from "@aws-amplify/core";
import { aws4Interceptor } from "aws4-axios";
import axios from "axios";
import { Credentials } from "@aws-sdk/types";
import { getReturnToUrl, setReturnToUrl } from "src/utils/userDeeplink";

const USER = "user";
const TOKEN = "token";
const ASSETS_LAST_MODIFIED = "assets-last-modified";

const TEST_USER_PREFIX = "svc-mw";
const getCurrentUser = async (): Promise<CognitoUser | null> => {
  try {
    return await Auth.currentAuthenticatedUser();
  } catch (e) {
    console.error("getCurrentUser err: ", e);
    return null;
  }
};

const signIn = () => {
  const savedDeeplink = getReturnToUrl();
  const href =
    savedDeeplink ??
    window.location.pathname + window.location.search + window.location.hash;
  setReturnToUrl(href);
  return Auth.federatedSignIn({
    customProvider: "FederateOIDC",
    customState: href,
  }).then((res) => {
    console.info("new session created / user signed in");
  });
};

let credentials: Credentials;

const login = async (shouldRefreshAccessKeyAndSecretKey: boolean) => {
  const stage: string = "gamma";
  if (stage !== "test") {
    const lastModified = await getAssetsLastModified();
    if (localStorage.getItem(ASSETS_LAST_MODIFIED) === null) {
      localStorage.setItem(ASSETS_LAST_MODIFIED, lastModified);
    } else if (localStorage.getItem(ASSETS_LAST_MODIFIED) !== lastModified) {
      localStorage.removeItem(ASSETS_LAST_MODIFIED);
      refreshPage();
    }
  }

  if (shouldRefreshAccessKeyAndSecretKey) {
    // Access keys and secret keys expire after an hour. If they are not refreshed, the user will keep getting an
    // error message saying that their token is expired/invalid when navigating the Ergona app. Forcing a page reload
    // refreshes these keys.
    window.location.reload();
  }

  const currentUser = {
    alias: "",
    token: "",
    lastName: "",
    firstName: "",
    email: "",
    country: "",
    ldap: [""],
    homeSite: "",
  };

  const user = await getCurrentUser();

  credentials = await Auth.currentCredentials();

  if (!user) {
    console.info("No session found, attempting to start a new one...");
    await signIn();
    console.log("user signed in");
  }

  if (user) {
    user.getSession((_: Error | null, session: CognitoUserSession) => {
      if (session) {
        const token = session.getIdToken().getJwtToken();
        currentUser.token = token;
        const decodeToken = session.getIdToken().decodePayload();
        currentUser.alias = decodeToken?.identities?.[0]?.userId ?? "test";
        currentUser.ldap = decodeToken["custom:LDAP_GROUP"]?.split(",");
        currentUser.firstName = decodeToken["given_name"];
        currentUser.lastName = decodeToken["family_name"];
        currentUser.email = decodeToken["email"];
        currentUser.country = decodeToken["custom:CO"];
        currentUser.homeSite = decodeToken["custom:AMZN_LOC_DESCR"]
          .split("-")[0]
          .trim();
        localStorage.setItem("token", token);
        // Updating the test user's ldap before api interceptor
        if (currentUser.alias?.startsWith(TEST_USER_PREFIX)) {
          currentUser.ldap = [
            "ergona-user",
            "ergona-user-eu",
            "ergona-user-fe",
            "ergona-job-rotation-user",
            "ergona-superuser",
          ];
        }
        const { accessKeyId, secretAccessKey, sessionToken } = credentials;
        const interceptor = aws4Interceptor(
          {
            region: "us-west-2",
            service: "execute-api",
          },
          {
            accessKeyId: accessKeyId,
            secretAccessKey: secretAccessKey,
            sessionToken: sessionToken,
          }
        );
        apiClient.interceptors.request.use(interceptor);
      }
    });
  }
  return currentUser;
};

const getAssetsLastModified = async () => {
  const res = await axios.head(`https://gamma.ergona.whs.amazon.dev`);
  return res.headers["last-modified"];
};

const refreshPage = async () => {
  console.log("assets changed, refresh page...");
  window.location.reload();
};

const logout = () => {
  localStorage.removeItem(TOKEN);
};

export default {
  login,
  logout,
  getCurrentUser,
  signIn,
};
